import InfoIcon from 'assets/icons/info.svg';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import { SegmentationIds } from 'constants/segmentation';
import { useReviewsTest } from 'contexts/reviews-test';
import { useSegmentationContext } from 'contexts/segmentation';
import useIsClient from 'hooks/common/use-is-client';
import useMobile from 'hooks/common/use-mobile';
import useModifyJudgeMe from 'hooks/product/judge-me/use-modify-judge-me';
import { StaticPDPProduct } from 'modules/product-details/types';
import dynamic from 'next/dynamic';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import style from './judge-me-reviews.module.css';
import useJudgeMeReviewsTracking from './use-judge-me-reviews-tracking';

const Sheet = dynamic(() => import('components/sheet'), {
  ssr: false,
});

const Tooltip = dynamic(() => import('components/tooltip'), {
  ssr: false,
});

interface Props {
  product: StaticPDPProduct;
}

/**
 * JudgeMeReviews component.
 * It's rendered in the Product Details page usign the JudgeMe API.
 */
const JudgeMeReviews: React.FC<Props> = ({ product }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { isClient } = useIsClient();
  const isMobile = useMobile();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const { segmentation } = useSegmentationContext();

  const { loading5970, loading5971, testData5970, testData5971, variant5971 } =
    useReviewsTest();

  const widgetRef = useModifyJudgeMe({
    product,
    segmentId: segmentation as SegmentationIds,
    loading5971,
    data5971: testData5971,
    variant5971,
    loading5970,
    data5970: testData5970,
  });
  useJudgeMeReviewsTracking(widgetRef);

  const handleInfoClick = (): void => {
    if (isMobile) {
      setIsOpen(true);
    } else {
      setShowTooltip(!showTooltip);
    }
  };

  if (!isClient) return null;

  return (
    <>
      <ImpressionTrackerNew element="PDP Reviews">
        <div
          className={`container ${style['reviews-container']}`}
          data-test="reviews-container"
        >
          <div className="-mb-2 flex items-center justify-between lg:justify-start lg:gap-2">
            <p className="text-text-light-bg-primary font-black">
              <FormattedMessage id="pdp:judge-me-reviews:tooltip:title" />
            </p>
            <button
              ref={infoButtonRef}
              onClick={handleInfoClick}
              data-test="reviews-info-button"
              aria-label="Review verification info"
              aria-describedby={showTooltip ? 'tooltip-content' : undefined}
            >
              <InfoIcon className="text-action-active h-6 w-6" />
            </button>
          </div>
          <div
            className="jdgm-widget jdgm-review-widget jdgm-outside-widget text-grey-700 font-sans"
            data-id={product.id}
            data-product-title={product.title}
            ref={widgetRef}
          />
        </div>
      </ImpressionTrackerNew>

      {/* Desktop Tooltip */}
      {!isMobile && (
        <Tooltip
          isOpen={showTooltip}
          targetRef={infoButtonRef}
          onClose={() => setShowTooltip(false)}
        >
          <p
            className="text-text-light-bg-primary text-center text-sm"
            data-test="reviews-tooltip-description"
          >
            <FormattedMessage id="pdp:judge-me-reviews:tooltip:description" />
          </p>
        </Tooltip>
      )}

      {/* Mobile Sheet */}
      <Sheet isOpen={isOpen && isMobile} onClose={() => setIsOpen(false)}>
        <div className="px-4 pb-8">
          <p className="text-text-light-bg-primary mb-2 text-center font-black">
            <FormattedMessage id="pdp:judge-me-reviews:tooltip:title" />
          </p>
          <p className="text-text-light-bg-secondary text-sm leading-tight">
            <FormattedMessage id="pdp:judge-me-reviews:tooltip:description" />
          </p>
        </div>
      </Sheet>
    </>
  );
};

export default JudgeMeReviews;
